import accountService from '@/services/account';
import collaboratorsService from '@/services/collaborators';
import expenseReceiptsService from '@/services/expenseReceipts';
import generalCatalogsService from '@/services/generalCatalogs';
import financialAccountRepository from '@/services/financialAccountRepository';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { formatCodeName } from '@/utils/textValue';
import { normalizeFiscalCodeOptions } from '@/composables/invoices/create/helpers';
import {
  buildPaymentAccountOptions,
  resolveDefaultPaymentAccount,
  resolveDefaultTransferPaymentMethod,
} from '@/composables/pay/usePayModalShared';

const resolveToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeVoucherType = (value = 'cfdi') => String(value || 'cfdi').trim().toLowerCase();

const normalizeCurrencyCode = (value = 'MXN') => String(value || 'MXN').trim().toUpperCase() || 'MXN';

const resolveNumericValue = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolvePendingAmountByCurrency = (source = {}, currency = 'MXN') => {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const directMatch = resolveNumericValue(source?.pendingByCurrency?.[normalizedCurrency]);
  if (directMatch > 0) {
    return directMatch;
  }

  const candidateValues = [source?.pendingAmount, source?.pending_amount, source?.pending, source?.balance];

  for (let index = 0; index < candidateValues.length; index += 1) {
    const resolved = resolveNumericValue(candidateValues[index]);
    if (resolved > 0) {
      return resolved;
    }
  }

  return 0;
};

const resolveCreatedExpenseReceiptId = (responsePayload = {}) => {
  const id = Number(responsePayload?.expense_receipt?.id || 0) || 0;
  return id > 0 ? id : 0;
};

const mapSupplierPendingSelectorOption = (item = {}) => {
  const pendingSource = item?.pending_by_currency && typeof item.pending_by_currency === 'object'
    ? item.pending_by_currency
    : {};

  const pendingByCurrency = Object.entries(pendingSource).reduce((accumulator, [currency, amount]) => {
    const normalizedCurrency = normalizeCurrencyCode(currency);
    accumulator[normalizedCurrency] = resolveNumericValue(amount);
    return accumulator;
  }, {});

  return {
    value: Number(item?.supplier_id || item?.id || 0),
    label: String(item?.supplier_name || item?.name || '').trim(),
    meta: {
      id: Number(item?.supplier_id || item?.id || 0),
      supplierId: Number(item?.supplier_id || item?.id || 0),
      pendingByCurrency,
      pendingAmount: resolveNumericValue(item?.pending_amount),
    },
  };
};

const mapCollaboratorPendingSelectorOption = (item = {}) => {
  const pendingSource = item?.pending_by_currency && typeof item.pending_by_currency === 'object'
    ? item.pending_by_currency
    : {};

  const pendingByCurrency = Object.entries(pendingSource).reduce((accumulator, [currency, amount]) => {
    const normalizedCurrency = normalizeCurrencyCode(currency);
    accumulator[normalizedCurrency] = resolveNumericValue(amount);
    return accumulator;
  }, {});

  return {
    value: Number(item?.collaborator_id || item?.id || 0),
    label: String(item?.collaborator_name || item?.name || '').trim(),
    meta: {
      id: Number(item?.collaborator_id || item?.id || 0),
      collaboratorId: Number(item?.collaborator_id || item?.id || 0),
      pendingByCurrency,
      pendingAmount: resolveNumericValue(item?.pending_amount),
    },
  };
};

const mapCollaboratorOption = (item = {}) => ({
  value: Number(item?.id || 0),
  label: String(item?.name || '').trim(),
  meta: {
    id: Number(item?.id || 0),
    collaboratorId: Number(item?.id || 0),
    pendingByCurrency: {},
    pendingAmount: 0,
  },
});

const resolveDocumentTypeFromRow = (row = {}) => {
  const voucherType = normalizeVoucherType(
    row?.voucherType
    || row?.voucher_type
    || row?.type
  );

  if (voucherType === 'simple_note') {
    return 'simple_note';
  }

  if (voucherType === 'foreign') {
    return 'foreign';
  }

  return 'cfdi';
};

const mapPendingDocument = (row = {}, index = 0) => {
  const rawId = row?.id || row?.payment_plan_id || '';
  const numericId = Number(rawId || 0) || 0;
  const id = String(rawId || '').trim() !== '' ? rawId : (numericId > 0 ? numericId : `temp-${index}`);
  const total = resolveNumericValue(row?.total || row?.amount || row?.subtotal);
  const pendingAmount = resolveNumericValue(
    row?.pendingAmount
    || row?.pending_amount
    || row?.pending
    || row?.balance
    || row?.remaining
  );

  const folio = String(row?.folio || row?.reference || '').trim();

  return {
    id,
    paymentId: Number(row?.payment_id || row?.paymentId || 0) || 0,
    folio,
    reference: folio,
    date: String(row?.date || row?.invoice_date || row?.document_date || '').trim(),
    type: resolveDocumentTypeFromRow(row),
    typeLabel: String(row?.typeLabel || row?.voucher_type_label || row?.voucherTypeLabel || '').trim(),
    paymentMethodCode: String(row?.payment_method_code || row?.paymentMethodCode || '').trim(),
    invoiceId: Number(row?.invoice_id || row?.invoiceId || 0) || 0,
    saleId: Number(row?.sale_id || row?.saleId || 0) || 0,
    saleCode: String(row?.sale_code || row?.saleCode || '').trim(),
    businessName: String(row?.business_name || row?.businessName || '').trim(),
    businessLabel: String(row?.business_label || row?.businessLabel || '').trim(),
    customerId: Number(row?.customer_id || row?.customerId || 0) || 0,
    customerName: String(row?.customer_name || row?.customerName || '').trim(),
    expenseClass: String(row?.expense_class || row?.expenseClass || '').trim(),
    category: String(row?.category || row?.expense_category || '').trim(),
    concept: String(row?.concept || '').trim(),
    concepts: Array.isArray(row?.concepts) ? row.concepts : [],
    total,
    pendingAmount,
    appliedAmount: 0,
    isLiquidated: false,
    currency: normalizeCurrencyCode(row?.currency || 'MXN'),
  };
};

const resolveBusinessSummary = (row = {}) => {
  const customerName = String(row?.customerName || row?.customer_name || '').trim();
  const businessName = formatCodeName(
    row?.saleCode || row?.sale_code,
    row?.businessName || row?.business_name,
    row?.businessLabel || row?.business_label,
  ).trim();

  if (customerName === '' && businessName === '') {
    return '';
  }

  if (customerName !== '' && businessName !== '') {
    return `${customerName}\n${businessName}`;
  }

  return customerName || businessName;
};

const rebalanceApplicationAllocations = (vm) => {
  const maxPayableAmount = Math.max(0, Number(vm?.info?.receivedAmount || 0) || 0);
  const rows = Array.isArray(vm?.pendingDocuments) ? vm.pendingDocuments : [];

  const normalizedRows = rows.map((row) => {
    const pendingAmount = Math.max(0, Number(row?.pendingAmount || 0) || 0);
    const appliedAmount = Math.max(0, Math.min(Number(row?.appliedAmount || 0) || 0, pendingAmount));

    return {
      ...row,
      pendingAmount,
      appliedAmount,
      isLiquidated: Boolean(row?.isLiquidated),
    };
  });

  const manualTotal = normalizedRows.reduce((accumulator, row) => {
    if (row.isLiquidated) {
      return accumulator;
    }

    return accumulator + row.appliedAmount;
  }, 0);

  let availableForLiquidation = Math.max(0, maxPayableAmount - manualTotal);

  vm.pendingDocuments = normalizedRows.map((row) => {
    if (!row.isLiquidated) {
      return row;
    }

    const nextAppliedAmount = Math.max(0, Math.min(row.pendingAmount, availableForLiquidation));
    availableForLiquidation -= nextAppliedAmount;

    return {
      ...row,
      appliedAmount: nextAppliedAmount,
    };
  });

  vm.totalAmount = (Array.isArray(vm.pendingDocuments) ? vm.pendingDocuments : [])
    .reduce((accumulator, row) => accumulator + (Number(row?.appliedAmount || 0) || 0), 0);
};

export const createExpenseReceiptCreateState = (text = {}) => ({
  headerActions: [
    {
      key: 'back',
      label: text?.create?.headerActions?.back || 'Regresar',
      icon: 'fa-arrow-left',
      variant: 'secondary',
    },
  ],
  currency: 'MXN',
  exchangeRate: 1,
  submitting: false,
  issuer: {
    businessName: '',
    rfc: '',
    taxRegime: '',
    fiscalResidence: '',
  },
  supplierOptions: [],
  collaboratorOptions: [],
  selectedSupplier: null,
  selectedCollaborator: null,
  taxRegimeOptions: [],
  cfdiUseOptions: [],
  paymentMethodTypeOptions: [],
  paymentFormOptions: [],
  paymentAccountOptions: [],
  serialOptions: [],
  info: {
    voucherType: 'cfdi',
    payTo: 'supplier',
    date: resolveToday(),
    serialId: 0,
    series: 'PE',
    folio: text?.create?.ui?.infoCard?.emptyValue || '-',
    cfdiFolio: text?.create?.ui?.infoCard?.emptyValue || '-',
    paymentForm: '',
    operationNumber: '',
    paymentAccountId: '',
    receivedAmount: 0,
  },
  supplierPendingAmount: 0,
  pendingDocuments: [],
  loadingPendingDocuments: false,
  totalAmount: 0,
  showSubmitConfirmModal: false,
  submitConfirmMode: 'normal',
});

export const expenseReceiptCreateComputed = {
  documentTypeOptions() {
    return [
      {
        value: 'cfdi',
        label: this.text.create.ui.infoCard.voucherTypes.cfdi,
      },
      {
        value: 'simple_note',
        label: this.text.create.ui.infoCard.voucherTypes.simpleNote,
      },
      {
        value: 'foreign',
        label: this.text.create.ui.infoCard.voucherTypes.foreign || 'Comp. Extranjero',
      },
    ];
  },
  isCfdiMode() {
    return String(this.info?.voucherType || 'cfdi').trim().toLowerCase() === 'cfdi';
  },
  filteredSupplierOptions() {
    return Array.isArray(this.supplierOptions) ? this.supplierOptions : [];
  },
  applicationDocuments() {
    const type = normalizeVoucherType(this.info?.voucherType);
    const currency = normalizeCurrencyCode(this.currency);
    const rows = Array.isArray(this.pendingDocuments) ? this.pendingDocuments : [];

    return rows.filter((row) => {
      const rowType = normalizeVoucherType(row?.type || 'cfdi');
      const rowCurrency = normalizeCurrencyCode(row?.currency || currency);
      return rowType === type && rowCurrency === currency;
    });
  },
  disableSubmitCfdi() {
    return !this.isCfdiMode;
  },
  appliedTotalAmount() {
    return (Array.isArray(this.applicationDocuments) ? this.applicationDocuments : [])
      .reduce((accumulator, row) => accumulator + (Number(row?.appliedAmount || 0) || 0), 0);
  },
  selectedApplicationItems() {
    return (Array.isArray(this.applicationDocuments) ? this.applicationDocuments : [])
      .filter((row) => Number(row?.paymentId || 0) > 0)
      .filter((row) => Number(row?.appliedAmount || 0) > 0);
  },
  submitSummary() {
    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();
    const counterpart = payTo === 'collaborator' ? this.selectedCollaborator : this.selectedSupplier;
    const supplierName = String(counterpart?.label || '').trim();
    const voucherType = String(this.info?.voucherType || 'cfdi').trim().toLowerCase();
    const voucherTypeLabel = voucherType === 'simple_note'
      ? String(this.text?.create?.ui?.infoCard?.voucherTypes?.simpleNote || 'Nota simple').trim()
      : voucherType === 'foreign'
        ? String(this.text?.create?.ui?.infoCard?.voucherTypes?.foreign || 'Comp. Extranjero').trim()
        : String(this.text?.create?.ui?.infoCard?.voucherTypes?.cfdi || 'CFDI').trim();

    const paymentFormCode = String(this.info?.paymentForm || '').trim();
    const paymentFormMatch = (Array.isArray(this.paymentFormOptions) ? this.paymentFormOptions : [])
      .find((option) => String(option?.value || '').trim() === paymentFormCode);

    const paymentAccountId = String(this.info?.paymentAccountId || '').trim();
    const paymentAccountMatch = (Array.isArray(this.paymentAccountOptions) ? this.paymentAccountOptions : [])
      .find((option) => String(option?.value || '').trim() === paymentAccountId);

    return {
      supplierName,
      voucherTypeLabel,
      counterpartLabel: payTo === 'collaborator'
        ? String(this.text?.create?.confirmModal?.labels?.collaborator || 'Colaborador').trim()
        : String(this.text?.create?.confirmModal?.labels?.supplier || 'Proveedor').trim(),
      paymentInfo: {
        date: String(this.info?.date || '').trim(),
        paymentForm: paymentFormCode,
        paymentFormLabel: String(paymentFormMatch?.label || paymentFormCode || '').trim(),
        paymentAccount: paymentAccountId,
        paymentAccountLabel: String(paymentAccountMatch?.label || paymentAccountId || '').trim(),
        operationNumber: String(this.info?.operationNumber || '').trim(),
      },
      items: this.selectedApplicationItems.map((row) => ({
        id: String(row?.id || ''),
        reference: String(row?.folio || row?.reference || '').trim(),
        expenseClass: String(row?.expenseClass || row?.expense_class || '').trim(),
        category: String(row?.category || '').trim(),
        business: resolveBusinessSummary(row),
        concept: String(row?.concept || '').trim(),
        amount: Number(row?.appliedAmount || 0) || 0,
      })),
      totalAmount: this.appliedTotalAmount,
    };
  },
};

export const expenseReceiptCreateMethods = {
  async initializePage() {
    await Promise.all([
      this.loadIssuerTaxInformation(),
      this.loadExpenseReceiptCodePreview(),
      this.loadCounterpartPendingSelectors(),
      this.loadCatalogs(),
    ]);
  },
  async loadExpenseReceiptCodePreview() {
    try {
      const response = await expenseReceiptsService.codePreview();
      const payload = extractPayload(response);
      const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      const prefix = String(data?.prefix || 'PE').trim() || 'PE';
      const folio = String(data?.folio || '').trim();

      this.info = {
        ...this.info,
        series: prefix,
        folio: folio || this.text?.create?.ui?.infoCard?.emptyValue || '-',
        cfdiFolio: `${prefix}${folio}`.trim() || this.text?.create?.ui?.infoCard?.emptyValue || '-',
      };
    } catch (error) {
      this.info = {
        ...this.info,
        series: 'PE',
      };
    }
  },
  async loadIssuerTaxInformation() {
    try {
      const response = await accountService.getTaxInformation();
      const payload = extractPayload(response);
      const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      const businessName = String(data?.business_name || data?.name || '').trim();
      const rfc = String(data?.rfc || '').trim();

      if (businessName === '' && rfc === '') {
        return;
      }

      this.issuer = {
        ...this.issuer,
        businessName: businessName !== '' ? businessName : this.issuer.businessName,
        rfc: rfc !== '' ? rfc : this.issuer.rfc,
        taxRegime: String(data?.regimen_label || data?.regimen || '').trim() || this.issuer.taxRegime,
        fiscalResidence: String(data?.fiscal_residence || data?.zipcode || '').trim() || this.issuer.fiscalResidence,
      };
    } catch (error) {
      // Keep fallback issuer values when endpoint is unavailable.
    }
  },
  async loadCounterpartPendingSelectors() {
    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();

    try {
      const response = await expenseReceiptsService.counterpartPendingSelectors({
        voucher_type: this.info?.voucherType || 'cfdi',
        currency: this.currency,
        pay_to: payTo,
      });

      const mapped = extractCollection(response)
        .map((item) => (payTo === 'collaborator'
          ? mapCollaboratorPendingSelectorOption(item)
          : mapSupplierPendingSelectorOption(item)))
        .filter((option) => Number(option?.value || 0) > 0)
        .filter((option) => resolvePendingAmountByCurrency(option?.meta || option, this.currency) > 0);

      if (payTo === 'collaborator') {
        this.collaboratorOptions = mapped;

        const selectedStillExists = mapped.some(
          (option) => Number(option?.value || 0) === Number(this.selectedCollaborator?.value || 0)
        );

        if (!selectedStillExists) {
          this.selectedCollaborator = null;
        } else {
          this.selectedCollaborator = mapped.find(
            (option) => Number(option?.value || 0) === Number(this.selectedCollaborator?.value || 0)
          ) || null;
        }
      } else {
        this.supplierOptions = mapped;

        const selectedStillExists = mapped.some(
          (option) => Number(option?.value || 0) === Number(this.selectedSupplier?.value || 0)
        );

        if (!selectedStillExists) {
          this.selectedSupplier = null;
        } else {
          this.selectedSupplier = mapped.find(
            (option) => Number(option?.value || 0) === Number(this.selectedSupplier?.value || 0)
          ) || null;
        }
      }
    } catch (error) {
      if (payTo === 'collaborator') {
        this.collaboratorOptions = [];
        this.selectedCollaborator = null;
      } else {
        this.supplierOptions = [];
        this.selectedSupplier = null;
      }
    }
  },
  async loadCatalogs() {
    const results = await Promise.allSettled([
      generalCatalogsService.getFormasPago(),
      financialAccountRepository.selectors(),
      collaboratorsService.selectors(),
    ]);

    const formasPagoResult = results[0];
    const paymentAccountsResult = results[1];
    const collaboratorsResult = results[2];

    this.paymentFormOptions = formasPagoResult?.status === 'fulfilled'
      ? normalizeFiscalCodeOptions(extractCollection(formasPagoResult.value))
      : [];

    const defaultTransfer = resolveDefaultTransferPaymentMethod(this.paymentFormOptions);
    if (defaultTransfer) {
      this.info = {
        ...this.info,
        paymentForm: String(defaultTransfer?.value || '').trim(),
      };
    }

    if (paymentAccountsResult?.status === 'fulfilled') {
      this.paymentAccountOptions = buildPaymentAccountOptions(
        extractCollection(paymentAccountsResult.value),
        'Sin cuenta asignada'
      );

      const defaultPaymentAccount = resolveDefaultPaymentAccount(this.paymentAccountOptions);
      this.info = {
        ...this.info,
        paymentAccountId: String(defaultPaymentAccount?.value || '').trim(),
      };
    } else {
      this.paymentAccountOptions = [];
      this.info = {
        ...this.info,
        paymentAccountId: '',
      };
    }

    this.collaboratorOptions = collaboratorsResult?.status === 'fulfilled'
      ? extractCollection(collaboratorsResult.value)
        .map((row) => mapCollaboratorOption(row))
        .filter((option) => Number(option?.value || 0) > 0)
      : [];
  },
  async selectSupplier(supplier) {
    this.selectedSupplier = supplier && typeof supplier === 'object' ? supplier : null;

    this.syncSupplierPendingAmount();
    await this.loadPendingDocumentsForSelection();
  },
  async selectCollaborator(collaborator) {
    this.selectedCollaborator = collaborator && typeof collaborator === 'object' ? collaborator : null;

    this.syncSupplierPendingAmount();
    await this.loadPendingDocumentsForSelection();
  },
  async onInfoChange(payload) {
    const key = String(payload?.key || '').trim();
    if (key === '') {
      return;
    }

    this.info = {
      ...this.info,
      [key]: payload?.value,
    };

    if (key === 'receivedAmount') {
      rebalanceApplicationAllocations(this);
    }

    if (key === 'voucherType') {
      this.supplierPendingAmount = 0;

      await this.loadCounterpartPendingSelectors();

      this.syncSupplierPendingAmount();
      await this.loadPendingDocumentsForSelection();
      return;
    }

    if (key === 'payTo') {
      const normalizedPayTo = String(payload?.value || '').trim().toLowerCase() === 'collaborator'
        ? 'collaborator'
        : 'supplier';

      this.info = {
        ...this.info,
        payTo: normalizedPayTo,
      };

      this.supplierPendingAmount = 0;
      await this.loadCounterpartPendingSelectors();

      this.syncSupplierPendingAmount();
      await this.loadPendingDocumentsForSelection();
      return;
    }
  },
  async onCurrencyChange(currency) {
    this.currency = normalizeCurrencyCode(currency);
    this.supplierPendingAmount = 0;
    await this.loadCounterpartPendingSelectors();
    this.syncSupplierPendingAmount();
    await this.loadPendingDocumentsForSelection();
  },
  onExchangeRateChange(rate) {
    const parsed = Number(rate || 0);
    this.exchangeRate = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  },
  async loadPendingDocumentsForSelection() {
    this.loadingPendingDocuments = true;

    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();
    const supplierId = Number(this.selectedSupplier?.value || 0);
    const collaboratorId = Number(this.selectedCollaborator?.value || 0);
    const hasTarget = payTo === 'collaborator' ? collaboratorId > 0 : supplierId > 0;

    if (!hasTarget) {
      this.pendingDocuments = [];
      this.totalAmount = 0;
      this.loadingPendingDocuments = false;
      return;
    }

    try {
      const response = await expenseReceiptsService.applicationItems({
        supplier_id: supplierId,
        collaborator_id: collaboratorId,
        pay_to: payTo,
        voucher_type: this.info?.voucherType || 'cfdi',
        currency: this.currency,
      });

      const rows = extractCollection(response);
      this.pendingDocuments = rows.map((row, index) => mapPendingDocument(row, index));
      rebalanceApplicationAllocations(this);
    } catch (error) {
      this.pendingDocuments = [];
      this.totalAmount = 0;
    } finally {
      this.loadingPendingDocuments = false;
    }
  },
  syncSupplierPendingAmount() {
    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();
    const selected = payTo === 'collaborator' ? this.selectedCollaborator : this.selectedSupplier;

    if (!selected) {
      this.supplierPendingAmount = 0;
      return;
    }

    const source = selected?.meta || selected;
    this.supplierPendingAmount = resolvePendingAmountByCurrency(source, this.currency);
  },
  onApplicationAmountChange(payload) {
    const id = payload?.id;
    const amount = Number(payload?.amount || 0) || 0;

    const currentDocuments = Array.isArray(this.pendingDocuments) ? this.pendingDocuments : [];
    const currentReceivedAmount = Math.max(0, Number(this.info?.receivedAmount || 0) || 0);

    const otherRowsAppliedTotal = currentDocuments.reduce((accumulator, row) => {
      if (String(row?.id) === String(id)) {
        return accumulator;
      }

      return accumulator + (Number(row?.appliedAmount || 0) || 0);
    }, 0);

    const maxAllowedByGeneralAmount = Math.max(0, currentReceivedAmount - otherRowsAppliedTotal);

    const nextDocuments = currentDocuments.map((row) => {
      if (String(row?.id) !== String(id)) {
        return row;
      }

      const maxAmount = Number(row?.pendingAmount || 0) || 0;
      const nextAppliedAmount = Math.max(0, Math.min(amount, maxAmount, maxAllowedByGeneralAmount));

      return {
        ...row,
        appliedAmount: nextAppliedAmount,
        isLiquidated: false,
      };
    });

    this.pendingDocuments = nextDocuments;

    rebalanceApplicationAllocations(this);
  },
  onToggleApplicationLiquidate(payload = {}) {
    const id = payload?.id;
    const shouldLiquidate = Boolean(payload?.value);

    if (shouldLiquidate && Number(this.info?.receivedAmount || 0) <= 0) {
      return;
    }

    this.pendingDocuments = (Array.isArray(this.pendingDocuments) ? this.pendingDocuments : []).map((row) => {
      if (String(row?.id) !== String(id)) {
        return row;
      }

      return {
        ...row,
        isLiquidated: shouldLiquidate,
        appliedAmount: shouldLiquidate ? (Number(row?.appliedAmount || 0) || 0) : 0,
      };
    });

    rebalanceApplicationAllocations(this);
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '').trim();
    if (actionKey !== 'back') {
      return;
    }

    this.goBack();
  },
  validateBeforeSubmit(shouldStamp = false) {
    if (String(this.info?.voucherType || '').trim().toLowerCase() === 'foreign') {
      this.$toast?.warning(
        String(
          this.text?.create?.validation?.foreignVoucherPending
          || 'El registro de pagos con comprobante extranjero estará disponible próximamente.'
        )
      );
      return false;
    }

    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();
    const hasCounterpart = payTo === 'collaborator'
      ? Boolean(this.selectedCollaborator)
      : Boolean(this.selectedSupplier);

    if (!hasCounterpart) {
      this.$toast?.error(
        payTo === 'collaborator'
          ? 'Selecciona un colaborador para registrar el pago.'
          : 'Selecciona un proveedor para registrar el pago.'
      );
      return false;
    }

    if (shouldStamp && !this.isCfdiMode) {
      this.$toast?.error(String(this.text?.create?.validation?.cfdiRequired || ''));
      return false;
    }

    if (this.selectedApplicationItems.length === 0) {
      this.$toast?.error(String(this.text?.create?.validation?.applicationRequired || ''));
      return false;
    }

    if (this.appliedTotalAmount <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.amountRequired || ''));
      return false;
    }

    const declaredAmount = Math.max(0, Number(this.info?.receivedAmount || 0) || 0);
    const appliedAmount = Math.max(0, Number(this.appliedTotalAmount || 0) || 0);
    if (declaredAmount > 0 && Math.abs(appliedAmount - declaredAmount) > 0.009999) {
      this.$toast?.warning(
        String(
          this.text?.create?.validation?.appliedAmountMismatch
          || 'Aún faltan importes por aplicar para completar el monto del pago. Revisa la aplicación antes de continuar.'
        )
      );
      return false;
    }

    return true;
  },
  buildPayload(shouldStamp = false) {
    const payTo = String(this.info?.payTo || 'supplier').trim().toLowerCase();
    const supplierId = Number(this.selectedSupplier?.value || this.selectedSupplier?.meta?.supplierId || 0) || 0;
    const collaboratorId = Number(this.selectedCollaborator?.value || this.selectedCollaborator?.meta?.collaboratorId || 0) || 0;

    return {
      document_type: 'P',
      voucher_type: String(this.info?.voucherType || 'cfdi').trim().toLowerCase(),
      pay_to: payTo,
      date: String(this.info?.date || '').trim(),
      payment_form: String(this.info?.paymentForm || '').trim(),
      operation_number: String(this.info?.operationNumber || '').trim(),
      payment_account_id: Number(this.info?.paymentAccountId || 0) || 0,
      amount: Number(this.appliedTotalAmount || 0) || 0,
      currency: String(this.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchange_rate: Number(this.exchangeRate || 1) || 1,
      supplier_id: supplierId,
      collaborator_id: collaboratorId,
      items: this.selectedApplicationItems.map((row) => ({
        payment_id: Number(row?.paymentId || 0) || 0,
        amount: Number(row?.appliedAmount || 0) || 0,
      })),
      should_stamp_payment: shouldStamp ? 1 : 0,
      stamp_payment: shouldStamp ? 1 : 0,
      timbrar: shouldStamp ? 1 : 0,
    };
  },
  openSubmitConfirmation(shouldStamp = false) {
    this.submitConfirmMode = shouldStamp ? 'cfdi' : 'normal';
    this.showSubmitConfirmModal = true;
  },
  closeSubmitConfirmation() {
    if (this.submitting) {
      return;
    }

    this.showSubmitConfirmModal = false;
  },
  async confirmSubmitPayment() {
    if (this.submitting) {
      return;
    }

    const shouldStamp = String(this.submitConfirmMode || '').trim().toLowerCase() === 'cfdi';
    if (!this.validateBeforeSubmit(shouldStamp)) {
      return;
    }

    const payload = this.buildPayload(shouldStamp);
    this.submitting = true;

    try {
      const response = await expenseReceiptsService.store(payload);
      const apiErrorMessage = extractApiErrorMessage(response, '');
      if (apiErrorMessage !== '') {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      const responsePayload = extractPayload(response);
      const createdExpenseReceiptId = resolveCreatedExpenseReceiptId(responsePayload);
      const successMessage = extractApiSuccessMessage(
        response,
        String(this.text?.create?.toast?.submitSuccess || 'Pago registrado correctamente.')
      );

      this.$toast?.success(successMessage);
      this.showSubmitConfirmModal = false;

      if (createdExpenseReceiptId > 0) {
        this.$router.push({
          name: 'expenseReceiptDetail',
          params: {
            expenseReceiptId: String(createdExpenseReceiptId),
          },
        }).catch(() => {});
        return;
      }

      this.goBack();
    } catch (error) {
      const message = String(error?.response?.data?.message || '').trim()
        || String(error?.message || '').trim()
        || String(this.text?.create?.toast?.submitError || 'No fue posible registrar el pago.');
      this.$toast?.error(message);
    } finally {
      this.submitting = false;
    }
  },
  submitPayment() {
    if (!this.validateBeforeSubmit(false)) {
      return;
    }

    this.openSubmitConfirmation(false);
  },
  submitPaymentCfdi() {
    this.submitPayment();
  },
  goBack() {
    this.$router.push({ name: 'expenseReceipts' });
  },
};
